import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import CalendarPicker from '../util/CalendarPicker';

// Категории (типы авто) — UA / EU / UK
export const LICENCE_CATEGORIES: { value: string; label: string }[] = [
  { value: 'AM', label: 'AM — Moped' },
  { value: 'A1', label: 'A1 — Light motorcycle' },
  { value: 'A2', label: 'A2 — Motorcycle' },
  { value: 'A', label: 'A — Motorcycle' },
  { value: 'B1', label: 'B1 — Quad / light vehicle' },
  { value: 'B', label: 'B — Car' },
  { value: 'B96', label: 'B96 — Car + trailer' },
  { value: 'BE', label: 'BE — Car + trailer' },
  { value: 'C1', label: 'C1 — Medium lorry' },
  { value: 'C1E', label: 'C1E — Medium lorry + trailer' },
  { value: 'C', label: 'C — Lorry' },
  { value: 'CE', label: 'CE — Lorry + trailer' },
  { value: 'D1', label: 'D1 — Minibus' },
  { value: 'D1E', label: 'D1E — Minibus + trailer' },
  { value: 'D', label: 'D — Bus' },
  { value: 'DE', label: 'DE — Bus + trailer' },
  { value: 'T', label: 'T — Tractor' },
  { value: 'F', label: 'F — Agricultural' },
  { value: 'G', label: 'G — Road roller' },
];

interface LicensePanelProps {
  userId: number | null;
  usersForPicker?: { value: number; label: string }[];
  baseUrl: string;
  onClose: () => void;
  onSaved: () => void;
}

const inputClass =
  'timesheet-edit-input bg-[var(--bg-input)] text-[var(--text-color)] rounded-[5px] py-[4px] px-[10px] text-[14px] w-full';
const labelClass = 'text-[12px] font-semibold text-[var(--text-color)]';

type PhotoSlot = { file: File | null; url: string; preview: string };
const emptySlot: PhotoSlot = { file: null, url: '', preview: '' };

const LicensePanel: React.FC<LicensePanelProps> = ({ userId, usersForPicker, baseUrl, onClose, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeUserId, setActiveUserId] = useState<number | null>(userId);
  const [number, setNumber] = useState('');
  const [issue, setIssue] = useState<string>('');
  const [expiry, setExpiry] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [front, setFront] = useState<PhotoSlot>(emptySlot);
  const [back, setBack] = useState<PhotoSlot>(emptySlot);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (uid: number) => {
    try {
      const { data } = await axios.get(`hr-driver-license/user/${uid}`);
      if (!data) return;
      setNumber(data.license_number || '');
      setIssue(data.issue_date ? String(data.issue_date).slice(0, 10) : '');
      setExpiry(data.expiry_date ? String(data.expiry_date).slice(0, 10) : '');
      setCategories(
        (data.vehicle_class || '')
          .split(',')
          .map((s: string) => s.trim())
          .filter(Boolean),
      );
      setFront({ file: null, url: data.photo_front_url || '', preview: '' });
      setBack({ file: null, url: data.photo_back_url || '', preview: '' });
    } catch {
      /* interceptor */
    }
  }, []);

  useEffect(() => { if (activeUserId != null) load(activeUserId); }, [activeUserId, load]);

  const pickPhoto = (setter: React.Dispatch<React.SetStateAction<PhotoSlot>>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setter({ file: f, url: '', preview: URL.createObjectURL(f) });
  };

  const status = useMemo(() => {
    const expDate = expiry ? new Date(expiry) : null;
    const hasAll = number.trim() && expDate;
    if (!hasAll) return { kind: 'none' as const, text: t('LicenceNotAdded') };
    const expired = expDate! < new Date();
    return expired
      ? { kind: 'bad' as const, text: `${t('Expired')} · ${expDate!.toLocaleDateString('en-GB')}` }
      : { kind: 'ok' as const, text: `${t('Active')} · ${t('Until')} ${expDate!.toLocaleDateString('en-GB')}` };
  }, [number, expiry]);

  const statusStyle: Record<string, React.CSSProperties> = {
    ok: { background: 'var(--color-success-bg)', color: 'var(--color-success)' },
    bad: { background: 'var(--color-danger-bg)', color: 'var(--color-danger)' },
    none: { background: 'var(--btn-user-in)', color: 'var(--text-color-calendar-week)' },
  };

  const handleSave = async () => {
    if (activeUserId == null) {
      enqueueSnackbar(t('SelectEmployee') || 'Select employee', { variant: 'warning' });
      return;
    }
    if (!number.trim() || !issue || !expiry || categories.length === 0) {
      enqueueSnackbar(t('FillRequiredFields') || 'Fill required fields', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await axios.put(`hr-driver-license/user/${activeUserId}`, {
        license_number: number.trim(),
        vehicle_class: categories.join(','),
        issue_date: issue,
        expiry_date: expiry,
      });

      const upload = async (slot: PhotoSlot, side: 'front' | 'back') => {
        if (!slot.file) return;
        const fd = new FormData();
        fd.append('file', slot.file);
        await axios.post(`hr-driver-license/user/${activeUserId}/photo-${side}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      };
      await upload(front, 'front');
      await upload(back, 'back');


      enqueueSnackbar(t('Saved') || 'Saved', { variant: 'success' });
      onSaved();
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || t('ErrorSaving') || 'Error saving', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const selectedCats = LICENCE_CATEGORIES.filter((c) => categories.includes(c.value));

  const PhotoBox: React.FC<{ slot: PhotoSlot; onPick: any; onClear: () => void; label: string }> = ({ slot, onPick, onClear, label }) => {
    const src = slot.preview || (slot.url ? `${baseUrl}${slot.url}` : '');
    return (
      <div>
        <label
          className="flex flex-col items-center justify-center gap-[6px] cursor-pointer relative overflow-hidden"
          style={{
            border: src ? '1.5px solid var(--color-success)' : '1.5px dashed var(--border-input)',
            borderRadius: 10, aspectRatio: '1.6 / 1', background: 'var(--btn-user-form)',
          }}
        >
          {src ? (
            <img src={src} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <>
              <span style={{ fontSize: 22, opacity: 0.7 }}>📷</span>
              <span className="text-[11px] font-semibold text-[var(--text-color)]">{label}</span>
              <span className="text-[10px] text-[var(--text-color-calendar-week)]">{t('UploadPhoto') || 'Upload photo'}</span>
            </>
          )}
          <input type="file" accept="image/*,.pdf" className="hidden" onChange={onPick} />
        </label>
        {src && (
          <button type="button" onClick={onClear} className="text-[11px] mt-[4px] text-[var(--color-danger)] cursor-pointer bg-transparent border-none">
            ✕ {t('Delete') || 'Remove'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full flex flex-col bg-[var(--bg-form)] text-[var(--text-color)]" style={{ minHeight: 0 }} role="presentation">
      <div className="flex justify-between items-center py-[10px] px-[15px] bg-[var(--bg-top)]" style={{ flexShrink: 0 }}>
        <p className="text-[16px] font-semibold">🪪 {t('DriverLicence') || 'Driving licence'}</p>
        <CloseIcon className="cursor-pointer" onClick={onClose} />
      </div>

      <div className="px-[15px] py-[12px] flex flex-col gap-[12px] overflow-auto flex-1" style={{ minHeight: 0 }}>
        {usersForPicker && (
          <div className="flex flex-col gap-[4px]">
            <span className="text-[12px] text-[var(--text-color-calendar-week)]">{t('SelectEmployee') || 'Select employee'} *</span>
            <Select
              autoFocus
              options={usersForPicker}
              placeholder={t('Search') || 'Search...'}
              value={usersForPicker.find((o) => o.value === activeUserId) ?? null}
              onChange={(opt: any) => { if (opt) setActiveUserId(opt.value); }}
              noOptionsMessage={() => t('NoEmployeesWithoutLicense') || 'All employees already have a licence'}
              styles={{
                control: (base) => ({ ...base, background: 'var(--bg-input)', borderColor: 'var(--border-input-color)', color: 'var(--text-color)' }),
                menu: (base) => ({ ...base, background: 'var(--bg-form)', zIndex: 9999 }),
                option: (base, s) => ({ ...base, background: s.isFocused ? 'var(--btn-user-hover)' : 'var(--bg-form)', color: 'var(--text-color)' }),
                singleValue: (base) => ({ ...base, color: 'var(--text-color)' }),
                input: (base) => ({ ...base, color: 'var(--text-color)' }),
              }}
            />
          </div>
        )}

        {activeUserId != null && status.kind !== 'none' && (
          <div className="flex items-center gap-[10px] px-[13px] py-[10px] rounded-[8px] text-[13px] font-bold" style={statusStyle[status.kind]}>
            {status.kind === 'ok' ? '✓' : '⚠'} {status.text}
          </div>
        )}

        <div className="flex flex-col gap-[4px]">
          <input title={t('LicenceNumber') as string} className={inputClass} value={number} onChange={(e) => setNumber(e.target.value)} placeholder={`${t('LicenceNumber')} *`} />
        </div>

        <div className="flex gap-[10px]">
          <div className="flex flex-col gap-[4px] flex-1">
            <CalendarPicker mode="single" variant="field" output="string" value={issue} onChange={(v: any) => setIssue(v)} displayFormat="DD/MM/YYYY" placeholder={`${t('IssueDate')} *`} />
          </div>
          <div className="flex flex-col gap-[4px] flex-1">
            <CalendarPicker mode="single" variant="field" output="string" value={expiry} onChange={(v: any) => setExpiry(v)} displayFormat="DD/MM/YYYY" placeholder={`${t('ExpiryDate')} *`} />
          </div>
        </div>

        <div className="flex flex-col gap-[4px]">
          <Select
            isMulti
            isClearable
            closeMenuOnSelect={false}
            placeholder={`${t('LicenceCategories')} *`}
            options={LICENCE_CATEGORIES}
            value={selectedCats}
            onChange={(opts) => setCategories(((opts ?? []) as unknown as { value: string }[]).map((o) => o.value))}
            className="basic-multi-select w-full bg-[var(--bg-input)]"
            classNamePrefix="select"
          />
        </div>

        <div>
          <span className={labelClass}>{t('LicencePhotos') || 'Licence photos'} <span className="text-[11px] text-[var(--text-color-calendar-week)] font-normal">({t('TwoSidesOptional') || 'front & back, optional'})</span></span>
          <div className="grid grid-cols-2 gap-[10px] mt-[8px]">
            <PhotoBox slot={front} onPick={pickPhoto(setFront)} onClear={() => setFront(emptySlot)} label={t('FrontSide') || 'Front side'} />
            <PhotoBox slot={back} onPick={pickPhoto(setBack)} onClear={() => setBack(emptySlot)} label={t('BackSide') || 'Back side'} />
          </div>
        </div>

        <div className="text-[11px] text-[var(--text-color-calendar-week)] leading-[1.5]">
          {t('LicenceHint') || 'Licence number, dates and categories are required. Photos are optional. When creating a Car Expense the system checks that the licence is added and valid.'}
        </div>
      </div>

      <div className="flex justify-end items-center gap-[10px] px-[15px] py-[10px] bg-[var(--bg-top-fix)]" style={{ flexShrink: 0, borderTop: 'var(--border-input)' }}>
        <button type="button" className="bg-[var(--btn-user-btn)] text-[var(--text-color)] border-none py-[7px] px-[16px] rounded-[5px] cursor-pointer hover:bg-[var(--btn-user-hover)]" onClick={onClose}>{t('Cancel') || 'Cancel'}</button>
        <button type="button" className="btn-primary" disabled={saving} onClick={handleSave}>💾 {t('Save') || 'Save'}</button>
      </div>
    </div>
  );
};

export default LicensePanel;
